import { isFunction } from 'lodash';
import { isRootNode } from '../../DynamicTree';

import { getNodeId } from '../store/actionHandlers/common/adapterActions';

const SELECTED = {
  selected: true,
  someChildrenSelected: true,
  allChildrenSelected: true,
};

const DESELECTED = {
  selected: false,
  someChildrenSelected: false,
  allChildrenSelected: false,
};

export default class MultiSelection {
  constructor({ nodeIsSelectable = () => true } = {}) {
    this._nodeIsSelectable = isFunction(nodeIsSelectable)
      ? nodeIsSelectable
      : () => nodeIsSelectable;
  }

  get key() {
    return 'selection';
  }

  get selectionVisuals() {
    return 'checkbox';
  }

  prepareTreeState(treeState) {
    treeState.singleSelectionMode = false;
    treeState.selection = [];
    treeState.multiselectStatuses = new Map();
  }

  getSelection(treeState) {
    return treeState.selection;
  }

  setSelection(treeState, selection, { adapter }) {
    treeState.selection = selection;
    this.syncSelectionStatuses(treeState, { adapter });
  }

  handleDidUpdateTree(treeState, { adapter }) {
    // Keep selection visuals in sync when tree/filter visibility changes.
    this.syncSelectionStatuses(treeState, { adapter });
  }

  syncSelectionStatuses(treeState, { adapter }) {
    const selection = Array.isArray(treeState.selection)
      ? treeState.selection
      : [];

    const multiselectStatuses = new Map();
    const selectedNodeIds = new Set();

    for (const node of selection) {
      const nodeId = getNodeId(adapter, node);
      selectedNodeIds.add(nodeId);
      // SETTING ONLY SELECTED NODES WITHOUT NESTED CHILDREN
      multiselectStatuses.set(nodeId, SELECTED);
      // setChildrenSelection(nodeId, treeState, SELECTED);
    }

    for (const nodeId of selectedNodeIds) {
      recomputeParents(
        nodeId,
        { ...treeState, multiselectStatuses },
        {
          isSelectionHydration: true,
        },
      );
    }

    treeState.multiselectStatuses = multiselectStatuses;
  }

  nodeIsSelected(treeState, nodeId) {
    return (
      this._nodeIsSelectable(treeState.models[nodeId], treeState.selection) &&
      (multiselectStatus(nodeId, treeState.multiselectStatuses).selected ||
        multiselectStatus(nodeId, treeState.multiselectStatuses)
          .allChildrenSelected)
    );
  }

  nodeIsAdvanced(treeState, nodeId) {
    const { selected, allChildrenSelected } = multiselectStatus(
      nodeId,
      treeState.multiselectStatuses,
    );
    return !selected && allChildrenSelected;
  }

  nodeIsIntermediate(
    { multiselectStatuses, context, children, hiddenNodeIds },
    nodeId,
  ) {
    const { selected, someChildrenSelected } = multiselectStatus(
      nodeId,
      multiselectStatuses,
    );

    const childrenCount = getChildrenCount(context, nodeId);
    const visibleChildIds = getVisibleChildIds(children[nodeId], hiddenNodeIds);
    const someVisibleChildrenSelected = hasSomeChildrenSelected(
      visibleChildIds,
      multiselectStatuses,
    );

    if (!someVisibleChildrenSelected) {
      return false;
    }

    if (
      Array.isArray(visibleChildIds) &&
      visibleChildIds.length < childrenCount &&
      someVisibleChildrenSelected
    ) {
      return !selected;
    }

    return !selected && someChildrenSelected;
  }

  nodeIsSelectable(node, selection) {
    return this._nodeIsSelectable(node, selection);
  }

  toggleNodeSelected(oldTreeState, nodeId) {
    if (this.nodeIsSelected(oldTreeState, nodeId)) {
      return {
        ...oldTreeState,
        ...deselectNode(oldTreeState, nodeId, this._nodeIsSelectable),
      };
    }
    return {
      ...oldTreeState,
      ...selectNode(oldTreeState, nodeId, this._nodeIsSelectable),
    };
  }
}

function selectNode(oldTreeState, nodeId, _nodeIsSelectable) {
  const {
    models,
    parents,
    children,
    multiselectStatuses: oldMultiselectStatuses,
    selection: oldSelection,
  } = oldTreeState;

  const multiselectStatuses = new Map(oldMultiselectStatuses);

  setChildrenSelection(
    nodeId,
    { ...oldTreeState, multiselectStatuses, children },
    SELECTED,
  );

  recomputeParents(nodeId, { ...oldTreeState, multiselectStatuses });

  return {
    selection: collectSelection(
      models,
      multiselectStatuses,
      oldSelection,
      _nodeIsSelectable,
    ),
    multiselectStatuses,
  };
}

function deselectNode(oldTreeState, nodeId, _nodeIsSelectable) {
  const {
    models,
    children,
    multiselectStatuses: oldMultiselectStatuses,
    selection: oldSelection,
  } = oldTreeState;

  const multiselectStatuses = new Map(oldMultiselectStatuses);

  setChildrenSelection(
    nodeId,
    { ...oldTreeState, multiselectStatuses, children },
    null,
  );

  recomputeParents(nodeId, { ...oldTreeState, multiselectStatuses });

  return {
    selection: collectSelection(
      models,
      multiselectStatuses,
      oldSelection,
      _nodeIsSelectable,
    ),
    multiselectStatuses,
  };
}

function setChildrenSelection(nodeId, treeState, status) {
  const { multiselectStatuses, children, context } = treeState;
  if (status) {
    multiselectStatuses.set(nodeId, status);
  } else {
    multiselectStatuses.delete(nodeId);
  }

  const childIds = children[nodeId];
  if (Array.isArray(childIds)) {
    const childrenCount = getChildrenCount(context, nodeId);

    const allChildrenSelected = children[nodeId].every(id => {
      const statuses = multiselectStatus(id, multiselectStatuses);
      return statuses.selected && statuses.allChildrenSelected;
    });

    const someChildrenSelected = children[nodeId].some(id => {
      const statuses = multiselectStatus(id, multiselectStatuses);
      return statuses.selected || statuses.someChildrenSelected;
    });

    if (
      status &&
      (childIds.length < childrenCount ||
        (!allChildrenSelected && someChildrenSelected))
    ) {
      multiselectStatuses.set(nodeId, {
        selected: allChildrenSelected,
        allChildrenSelected: false,
        someChildrenSelected: true,
      });
    }

    for (const childId of childIds) {
      setChildrenSelection(
        childId,
        { ...treeState, multiselectStatuses, children },
        status,
      );
    }

    const newAllChildrenSelected = children[nodeId].every(id => {
      const statuses = multiselectStatus(id, multiselectStatuses);
      return statuses.selected && statuses.allChildrenSelected;
    });

    if (status && newAllChildrenSelected && childIds.length === childrenCount) {
      multiselectStatuses.set(nodeId, SELECTED);
    }
  }
}

function recomputeParents(nodeId, treeState, options = {}) {
  const {
    multiselectStatuses,
    parents,
    children,
    context,
    hiddenNodeIds,
  } = treeState;
  const { isSelectionHydration = false } = options;
  const parentId = parents[nodeId];
  if (!parentId) {
    return;
  }

  const childrenCount = getChildrenCount(context, parentId);

  const parentStatus = multiselectStatus(parentId, multiselectStatuses);

  const childIds = children[parentId] || [];
  const visibleChildIds = getVisibleChildIds(childIds, hiddenNodeIds);
  const targetChildIds = visibleChildIds || childIds;

  let allChildrenSelected = hasAllChildrenSelected(
    targetChildIds,
    multiselectStatuses,
  );
  let someChildrenSelected = hasSomeChildrenSelected(
    targetChildIds,
    multiselectStatuses,
  );

  if (
    !hiddenNodeIds &&
    !isSelectionHydration &&
    Array.isArray(childIds) &&
    childIds.length < childrenCount
  ) {
    allChildrenSelected = false;
    someChildrenSelected = true;
  }

  const selected = allChildrenSelected;
  const normalizedAllChildrenSelected = selected;
  const normalizedSomeChildrenSelected = selected
    ? false
    : someChildrenSelected;

  if (
    parentStatus.selected !== selected ||
    parentStatus.allChildrenSelected !== normalizedAllChildrenSelected ||
    parentStatus.someChildrenSelected !== normalizedSomeChildrenSelected
  ) {
    multiselectStatuses.set(parentId, {
      selected,
      allChildrenSelected: normalizedAllChildrenSelected,
      someChildrenSelected: normalizedSomeChildrenSelected,
    });
    recomputeParents(parentId, { ...treeState, multiselectStatuses }, options);
  }
}

function collectSelection(
  models,
  multiselectStatuses,
  oldSelection,
  _nodeIsSelectable,
) {
  const result = [];

  for (const [id, status] of multiselectStatuses) {
    if (status.selected) {
      if (!isRootNode(models[id]) || _nodeIsSelectable(models[id])) {
        result.push(models[id] || oldSelection.find(org => org.id === id));
      }
    }
  }

  return result.filter(node => !!node);
}

function multiselectStatus(nodeId, multiselectStatuses) {
  return multiselectStatuses.get(nodeId) || DESELECTED;
}

function getChildrenCount(context, nodeId) {
  return context?.getMainTreeChildrenCounts
    ? context.getMainTreeChildrenCounts(nodeId)
    : 0;
}

function getVisibleChildIds(childIds, hiddenNodeIds) {
  if (!Array.isArray(childIds)) {
    return childIds;
  }
  return childIds.filter(id => !hiddenNodeIds?.[id]);
}

function hasAllChildrenSelected(childIds, multiselectStatuses) {
  return (
    Array.isArray(childIds) &&
    childIds.length > 0 &&
    childIds.every(id => {
      const statuses = multiselectStatus(id, multiselectStatuses);
      return statuses.selected && statuses.allChildrenSelected;
    })
  );
}

function hasSomeChildrenSelected(childIds, multiselectStatuses) {
  return (
    Array.isArray(childIds) &&
    childIds.some(id => {
      const statuses = multiselectStatus(id, multiselectStatuses);
      return statuses.selected || statuses.someChildrenSelected;
    })
  );
}
